'use client';

import React, { useState, useEffect } from 'react';
import FileManager from './FileManager';
import type { S3Object } from '../../variants/default/utils/services/fileManager';
import s3UploadService from '../../variants/default/utils/services/s3upload';

interface AudioFileManagerProps {
  namepath: string;
  onFileSelect?: (file: S3Object) => void;
  onUpload?: (files: File[]) => void;
}

export default function AudioFileManager({ 
  namepath, 
  onFileSelect, 
  onUpload 
}: AudioFileManagerProps) {
  const [uploading, setUploading] = useState(false);

  // Audio file extensions to filter
  const audioExtensions = ['.mp3', '.wav', '.ogg', '.m4a', '.aac'];

  // Handle file selection - only allow audio files
  const handleFileSelect = (file: S3Object) => {
    if (file.isFolder) {
      // For folders, we need to let the FileManager handle navigation
      // We don't call onFileSelect for folders, FileManager will handle internally
      return;
    }
    
    const isAudioFile = audioExtensions.some(ext => 
      file.name.toLowerCase().endsWith(ext)
    );
    
    if (!isAudioFile) {
      alert('Vui lòng chọn file audio (.mp3, .wav, .ogg, .m4a, .aac)');
      return;
    }
    
    onFileSelect?.(file);
  };

  // Handle file upload
  const handleUpload = async (files: File[]) => {
    if (!files || files.length === 0) return;
    
    setUploading(true);
    try {
      // Filter only audio files
      const audioFiles = files.filter(file => 
        audioExtensions.some(ext => file.name.toLowerCase().endsWith(ext))
      );
      
      if (audioFiles.length === 0) {
        alert('Vui lòng chọn file audio (.mp3, .wav, .ogg, .m4a, .aac)');
        return;
      }
      
      if (audioFiles.length !== files.length) {
        const rejected = files.length - audioFiles.length;
        alert(`${rejected} file không phải audio đã bị bỏ qua. Chỉ upload ${audioFiles.length} file audio.`);
      }

      // Call custom upload handler if provided, otherwise use default S3 upload
      if (onUpload) {
        onUpload(audioFiles);
      } else {
        // Default S3 upload to audio subfolder
        for (const file of audioFiles) {
          await s3UploadService.uploadToS3(namepath, {
            body: { file },
            subfolder: 'audio'
          });
        }
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Có lỗi xảy ra khi upload file');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="h-full">
      <FileManager
        namepath={namepath}
        onFileSelect={handleFileSelect}
        allowUpload={true}
        allowDelete={false}
        allowDownload={true}
      />
      
      {uploading && (
        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center">
          <div className="flex items-center gap-2">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="text-gray-700">Đang upload...</span>
          </div>
        </div>
      )}
    </div>
  );
}
